import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ApiRequestError, setBearerToken, setUnauthorizedHandler } from '@/lib/api/client';

import * as auth from './api';
import { AuthContext, type AuthContextValue } from './AuthContext';
import { clearSession, isAuthenticated, readSession, type Session, writeSession } from './session';

/** Owns the session and keeps the API client's bearer token in step with it. */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();

  const [session, setSession] = useState<Session | null>(() => readSession());
  const [user, setUser] = useState<auth.AdminUser | null>(null);
  const [needsReauth, setNeedsReauth] = useState(false);
  const [isRestoring, setIsRestoring] = useState(() => isAuthenticated(readSession()));

  // Before any request goes out, and before the restore effect below runs.
  setBearerToken(session?.token ?? null);

  const store = useCallback((next: Session | null) => {
    setSession(next);
    setBearerToken(next?.token ?? null);

    if (next === null) {
      clearSession();
    } else {
      writeSession(next);
    }
  }, []);

  const forget = useCallback(() => {
    store(null);
    setUser(null);
    setNeedsReauth(false);
    queryClient.clear();
  }, [queryClient, store]);

  /*
   * A 401 on an authenticated request. The modal is only worth raising when
   * there is something to come back to — with no session the guard has already
   * sent the user to the login screen.
   */
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setNeedsReauth((current) => current || isAuthenticated(readSession()));
    });

    return () => setUnauthorizedHandler(null);
  }, []);

  /*
   * A stored token is a claim, not a session. `me` is what turns it into one,
   * and it is also the only thing that notices a token the server has since
   * revoked — a deactivated account, or a logout from another device.
   */
  useEffect(() => {
    if (!isAuthenticated(session)) {
      setIsRestoring(false);

      return;
    }

    if (user !== null) {
      return;
    }

    let cancelled = false;

    void auth
      .fetchMe()
      .then((response) => {
        if (!cancelled) {
          setUser(response.data);
        }
      })
      .catch((error: unknown) => {
        /*
         * Only when the *server* refused it.
         *
         * A blanket catch here signs people out for things that say nothing
         * about the token: a dropped connection, and — the one that bites —
         * a navigation that aborts this request while it is still in flight.
         * `fetch` rejects with a TypeError, `forget()` clears localStorage, and
         * the page being navigated to loads with no session. Clicking a link
         * quickly enough after opening the dashboard logged you out.
         *
         * An `ApiRequestError` means a response arrived and was not 2xx, which
         * is the only evidence that the token itself is no good. The 401
         * handler has already raised §6.7's modal if it was one.
         */
        if (!cancelled && error instanceof ApiRequestError) {
          forget();
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsRestoring(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [session, user, forget]);

  const adoptSession = useCallback(
    (payload: auth.TokenPayload, nextUser?: auth.AdminUser) => {
      store({
        token: payload.token,
        status: payload.status,
        expiresAt: payload.expires_at,
      });

      if (nextUser) {
        setUser(nextUser);
      }
    },
    [store],
  );

  const signIn = useCallback(
    async ({ email, password }: { email: string; password: string }) => {
      const result = await auth.login({ email, password });

      adoptSession(result, result.user);

      return result.status;
    },
    [adoptSession],
  );

  const completeChallenge = useCallback(
    async (payload: { code?: string; recovery_code?: string }) => {
      const result = await auth.answerTwoFactorChallenge(payload);

      adoptSession(result, result.user);
    },
    [adoptSession],
  );

  const signOut = useCallback(async () => {
    try {
      await auth.logout();
    } catch {
      // A token the server has already dropped still ends the local session —
      // the point of the button is that the person stops being signed in.
    }

    forget();
  }, [forget]);

  const resumeSession = useCallback(() => {
    setNeedsReauth(false);
    void queryClient.refetchQueries();
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user,
      isRestoring,
      needsReauth,
      signIn,
      completeChallenge,
      adoptSession,
      signOut,
      resumeSession,
      updateUser: setUser,
      can: (permission) => user?.permissions.includes(permission) ?? false,
      hasRole: (role) => user?.roles.includes(role) ?? false,
    }),
    [
      session,
      user,
      isRestoring,
      needsReauth,
      signIn,
      completeChallenge,
      adoptSession,
      signOut,
      resumeSession,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
